import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  ButtonGroup,
  Tooltip,
  useTheme,
  LinearProgress,
} from "@mui/material";

import { Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { saveUserConfiguration } from "../../util/SaveUserConfigurations";
import {
  ArrowBackIosNewOutlined,
  Info,
  KeyboardArrowLeftOutlined,
  KeyboardArrowRightOutlined,
  KeyboardDoubleArrowRight,
  Looks3Outlined,
  Looks4Outlined,
  Looks5Outlined,
  LooksOneOutlined,
  LooksTwoOutlined,
} from "@mui/icons-material";
import { playCompleteSound } from "../../util/PlaySound";
import { ChromePicker, TwitterPicker } from "react-color";
import UploadFileModal from "../UploadFileModal.js/UploadFileModal";
import { addDataUrlPrefix } from "../../util/Base64Util";
import { iconsPath } from "../../variables/uris";
import { getSeatCode, getSeatDetails } from "../../util/getSeatCode";
// import completeSoundEffect from "../../../public/Assets/success.mp3";
const MotionBox = motion.create(Box);

const ConfiguratorTabs = ({
  data,
  groupedData,
  authUser,
  changeConfigs,
  dataSelection,
  setDataCateg,
  addToCustomFiles,
  customFiles,
  fireCameraAnimation,
  showReviewPage,
  retrievedDataSelection,
  recallRetrievedData = false,
  productBodyType,
  page,
  open,
  isNight = false,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [isOpen, setIsOpen] = useState(open); // NEW: controls open/close animation
  const [activeStep, setActiveStep] = useState(
    () => data?.[0]?.StepTitle || "",
  );
  const [activeSubStep, setActiveSubStep] = useState(
    () => data?.[0]?.SubStepTitle || "",
  );
  const [activeSubStepDivisions, setActiveSubStepDivisions] = useState({});
  const divisionPanelRefs = useRef({});
  const [divisionPanelWidths, setDivisionPanelWidths] = useState({});
  const theme = useTheme();
  const [openCascade, setOpenCascade] = useState([]);
  const [pendingPrerequisites, setPendingPrerequisites] = useState([]);

  const [steps] = useState(
    Array.from(new Set(data.map((item) => item.StepTitle))),
  );

  const [cmpltSndPlyd, setCmpltSndPlyd] = useState({});
  const [subSteps, setSubSteps] = useState(getSubSteps(activeStep));
  const [colorPickerValue, setColorPickerValue] = useState({});
  const [optionWithColorPicker, setOptionWithColorPicker] = useState({});
  const [colorPickerLeft, setColorPickerLeft] = useState(null);
  const [uploadModalOpenId, setUploadModalOpenId] = useState("");
  //
  const seatDetails = getSeatDetails(dataSelection);
  const seatTypeSelection = seatDetails.seatTypeSelection;
  const consoleWidthSelection = seatDetails.consoleWidthSelection;
  const seatCode = useMemo(
    () => getSeatCode(seatTypeSelection, consoleWidthSelection),
    [seatTypeSelection, consoleWidthSelection],
  );

  useEffect(() => {
    setDataCateg?.(activeSubStep);
  }, [activeSubStep, setDataCateg]);

  useEffect(() => {
    if (pendingPrerequisites.length === 0) return;

    const readyPrerequisites = [];
    const waitingPrerequisites = [];

    pendingPrerequisites.forEach((pendingPrerequisite) => {
      const variantID = pendingPrerequisite.variantIDWithColor.split("_#")[0];
      const option = data.find((item) => item.VariantID === variantID);

      if (!option) return;

      if (isOptionContextOpen(option, openCascade)) {
        readyPrerequisites.push({ option, ...pendingPrerequisite });
      } else {
        waitingPrerequisites.push(pendingPrerequisite);
      }
    });

    readyPrerequisites.forEach(({ option, variantIDWithColor }) => {
      applyPrerequisiteOption(option, variantIDWithColor);
    });

    if (readyPrerequisites.length > 0) {
      setPendingPrerequisites(waitingPrerequisites);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openCascade, pendingPrerequisites, data]);

  const nightTextColor = "#f4f6fa";
  const nightMutedTextColor = "#cfd6e2";
  const dayPrimaryTextColor = "#464647ff";
  const dayMutedTextColor = "#4f4f4f";

  const customPickerRef = useRef(null);
  useOutsidePicker(customPickerRef, onOptionSelection, optionWithColorPicker);

  const collinsPickerRef = useRef(null);
  useOutsidePicker(collinsPickerRef, onOptionSelection, optionWithColorPicker);

  useEffect(() => {
    const measureDivisionPanels = () => {
      const nextWidths = {};

      Object.entries(divisionPanelRefs.current).forEach(([key, node]) => {
        if (node) nextWidths[key] = node.scrollWidth;
      });

      setDivisionPanelWidths((prev) => {
        const prevKeys = Object.keys(prev);
        const nextKeys = Object.keys(nextWidths);
        const hasChanged =
          prevKeys.length !== nextKeys.length ||
          nextKeys.some((key) => prev[key] !== nextWidths[key]);

        return hasChanged ? nextWidths : prev;
      });
    };

    const frameId = window.requestAnimationFrame(measureDivisionPanels);
    window.addEventListener("resize", measureDivisionPanels);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", measureDivisionPanels);
    };
  });

  useEffect(() => {
    setSubSteps(getSubSteps(activeStep));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, activeStep]);

  function getSubSteps(step) {
    return Array.from(
      new Set(
        data
          .filter((item) => item.StepTitle === step)
          .map((item) => item.SubStepTitle),
      ),
    );
  }

  const [selectedByUser, setSelectedByUser] = useState(() => {
    return Object.fromEntries(
      Object.entries(dataSelection).map(([subStepTitle, parents]) => [
        subStepTitle,
        Object.fromEntries(
          Object.entries(parents).map(([cascadeName, contexts]) => [
            cascadeName,
            Object.fromEntries(
              Object.keys(contexts)
                // adjust condition if you only want some contexts
                .map((context) => {
                  let optionsInContext = data.filter(
                    (d) => d.Context === context,
                  );
                  if (optionsInContext.length === 1) return [context, true];
                  else return [context, false];
                }),
            ),
          ]),
        ),
      ]),
    );
  });

  useEffect(() => {
    if (isStepCompleted("step", activeStep) && !cmpltSndPlyd[activeStep]) {
      playCompleteSound("/assets/success.mp3");

      // Mark this step’s sound as played
      setCmpltSndPlyd((prev) => ({
        ...prev,
        [activeStep]: true,
      }));
    }
  }, [activeStep, cmpltSndPlyd, isStepCompleted]);
  //
  //
  //

  useEffect(() => {
    if (recallRetrievedData) {
      setSelectedByUser(
        Object.fromEntries(
          Object.entries(dataSelection).map(([subStepTitle, parents]) => [
            subStepTitle,
            Object.fromEntries(
              Object.entries(parents).map(([cascadeName, contexts]) => [
                cascadeName,
                Object.fromEntries(
                  Object.keys(contexts).map((context) => {
                    const bool =
                      subStepTitle in retrievedDataSelection &&
                      cascadeName in retrievedDataSelection[subStepTitle] &&
                      context in
                        retrievedDataSelection[subStepTitle][cascadeName];

                    // return [context, bool];
                    // const bool =
                    //   retrievedDataSelection?.[subStepTitle]?.[cascadeName]?.[
                    //     context
                    //   ] === true;

                    return [context, bool];
                  }),
                ),
              ]),
            ),
          ]),
        ),
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recallRetrievedData]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Enter") {
        pressNextOrReview();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [pressNextOrReview]);

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  // function isStepCompleted_() {
  //   return true;
  // }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  function pressNextOrReview() {
    if (isStepCompleted("subStep", activeSubStep)) {
      const indexOfNextStep = subSteps.indexOf(activeSubStep) + 1;
      if (indexOfNextStep < subSteps.length) {
        setActiveSubStep(subSteps[indexOfNextStep]);
        fireCameraAnimationForStep(subSteps[indexOfNextStep]);
      }
      if (
        subSteps.indexOf(activeSubStep) === subSteps.length - 1 &&
        isStepCompleted("subStep", activeSubStep)
      ) {
        if (steps[steps.indexOf(activeStep) + 1]) {
          let newActiveStep = steps[steps.indexOf(activeStep) + 1];
          let newSubSteps = Array.from(
            new Set(
              data
                .filter((item) => item.StepTitle === newActiveStep)
                .map((item) => item.SubStepTitle),
            ),
          );
          setActiveStep(newActiveStep);
          setSubSteps(newSubSteps);
          setActiveSubStep(newSubSteps[0]);
        } else {
          showReviewPage();
          fireCameraAnimation("-200|60|100");
        }
      }
    }
  }

  function handleBack() {
    const stepIndex = steps.indexOf(activeStep);

    // Get all substeps for the current step
    const substepsForCurrent = Array.from(
      new Set(
        data
          .filter((d) => d.StepTitle === activeStep)
          .map((d) => d.SubStepTitle)
          .filter(Boolean),
      ),
    );

    const substepIndex = substepsForCurrent.indexOf(activeSubStep);

    // 🟢 CASE 1: Go to previous substep within same step
    if (substepIndex > 0) {
      const prevSubStep = substepsForCurrent[substepIndex - 1];
      setActiveSubStep(prevSubStep);
      return;
    }

    // 🔵 CASE 2: We're at the first substep of this step — go to the previous step
    if (stepIndex > 0) {
      const prevStep = steps[stepIndex - 1];
      const substepsForPrevStep = Array.from(
        new Set(
          data
            .filter((d) => d.StepTitle === prevStep)
            .map((d) => d.SubStepTitle)
            .filter(Boolean),
        ),
      );

      // If previous step has substeps, go to its last one
      if (substepsForPrevStep.length > 0) {
        setActiveStep(prevStep);
        setActiveSubStep(substepsForPrevStep[substepsForPrevStep.length - 1]);
      } else {
        // Otherwise just go to the step itself (no substeps)
        setActiveStep(prevStep);
        setActiveSubStep(null);
      }

      return;
    }

    // 🔴 CASE 3: Already at the first step and substep — can’t go back
    console.warn("Already at the first step — cannot go back further.");
  }

  function canGoBack() {
    const stepIndex = steps.indexOf(activeStep);

    // Get all substeps for the current step
    const substepsForCurrent = Array.from(
      new Set(
        data
          .filter((d) => d.StepTitle === activeStep)
          .map((d) => d.SubStepTitle)
          .filter(Boolean),
      ),
    );

    const substepIndex = substepsForCurrent.indexOf(activeSubStep);

    // You can go back if:
    // 1️⃣ There’s a previous substep within the same step
    // 2️⃣ OR there’s a previous step (regardless of substeps)
    return substepIndex > 0 || stepIndex > 0;
  }

  // When user changes the configs
  function userSelection(SubStepTitle, CascadeName, context, unselect = false) {
    setSelectedByUser((prev) => {
      // clone current step object or start fresh
      const stepData = { ...(prev[SubStepTitle] || {}) };

      // clone current parent object or start fresh
      const parentData = { ...(stepData[CascadeName] || {}) };

      if (unselect) {
        // unselect the entry
        parentData[context] = false;
      } else {
        // select entry
        parentData[context] = true;
      }

      return {
        ...prev,
        [SubStepTitle]: {
          ...stepData,
          [CascadeName]: parentData,
        },
      };
    });
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  function isStepCompleted(stepType, step) {
    if (stepType === "step") {
      if (steps.indexOf(step) < steps.indexOf(activeStep)) return true;

      const substepsForThisStep = Array.from(
        new Set(
          data
            .filter((d) => d.StepTitle === step)
            .map((d) => d.SubStepTitle)
            .filter(Boolean),
        ),
      );

      if (substepsForThisStep.length > 0) {
        const allSubstepsCompleted = substepsForThisStep.every((sub) =>
          isStepCompleted("substep", sub),
        );
        if (allSubstepsCompleted) return true;
      }

      return false;
    } else {
      // SUBSTEP
      if (!step || !selectedByUser[step]) return false;
      const stepSelectedByUser = selectedByUser[step];

      // 1) All contexts selected
      const allSelected = Object.values(stepSelectedByUser).every((parent) =>
        Object.values(parent).every((contextBool) => contextBool === true),
      );
      if (allSelected) return true;

      // 2) Mandatory contexts (improved: include leaf cascaded items)
      let mandatoryVariants = data.filter((d) => {
        // treat MandatoryContext as string "TRUE"/"FALSE" or boolean
        const isMandatory =
          d.MandatoryContext === true ||
          String(d.MandatoryContext).toUpperCase() === "TRUE";
        return d.SubStepTitle === step && isMandatory;
      });

      // Keep only those that are active within openCascade (same behaviour as before)
      mandatoryVariants = mandatoryVariants.filter((mv) => {
        if (
          String(mv.Cascaded).toUpperCase() === "FALSE" ||
          mv.Cascaded === false
        )
          return true;
        return openCascade.some((oc) => Object.values(oc)[0] === mv.Context);
      });

      // If any mandatory variants exist, ensure each mandatory is selected
      let allMandatorySelected = false;
      if (mandatoryVariants.length > 0) {
        allMandatorySelected = mandatoryVariants.every((variant) => {
          const parentSelections = stepSelectedByUser[variant.CascadeName];

          if (!parentSelections) return false;
          // variant.Context may be a leaf context (e.g. "A330-NEO") or a parent context depending on CSV shape

          return !!parentSelections[variant.Context];
        });

        if (allMandatorySelected) return true;
        // If mandatory exist but not all selected, not complete
        return false;
      }

      // 3) Cascaded groups: require at least one selected per cascaded group
      // Build map: { CascadeName -> [variants...] } for cascaded variants under this substep
      const cascadedVariants = data.filter(
        (d) =>
          d.SubStepTitle === step &&
          (String(d.Cascaded).toUpperCase() === "TRUE" || d.Cascaded === true),
      );

      if (cascadedVariants.length > 0) {
        const cascadedGroups = cascadedVariants.reduce((acc, v) => {
          const key = v.CascadeName || "__no_cascade_name__";
          if (!acc[key]) acc[key] = [];
          acc[key].push(v);
          return acc;
        }, {});

        // Uncomment for debugging:
        // console.debug("isStepCompleted debug:", { step, stepSelectedByUser, cascadedGroups, mandatoryVariants });

        // For each cascaded group, require at least one selected (only for groups that are "active" via openCascade OR all)
        const eachGroupHasSelection = Object.entries(cascadedGroups).every(
          ([cascadeName, variants]) => {
            // Determine whether this cascade group is active (if any variant in group is active via openCascade OR group isn't cascaded via openCascade concept)
            const groupActive = variants.some((v) => {
              // if not further cascaded (Cascaded=TRUE means this row is a cascade row), we still treat group as active
              // If openCascade is used to limit active contexts, include that logic:
              if (
                String(v.Cascaded).toUpperCase() === "FALSE" ||
                v.Cascaded === false
              )
                return true;
              // If it is cascaded, check openCascade
              return openCascade.some(
                (oc) => Object.values(oc)[0] === v.Context,
              );
            });

            if (!groupActive) return true; // ignore inactive groups

            // Now check if any variant in that group is selected
            const anySelectedInGroup = variants.some((variant) => {
              const parentSelections = stepSelectedByUser[variant.CascadeName];
              if (!parentSelections) return false;
              return !!parentSelections[variant.Context];
            });

            return anySelectedInGroup;
          },
        );

        if (!eachGroupHasSelection) return false; // not complete if any active cascaded group has no selection
      }

      // 4) Last-cascaded leaf selection (allow completion when no mandatory variants and some leaf selected)
      const lastCascadedIsSelected = Object.entries(stepSelectedByUser).some(
        ([cascadeName, contexts]) => {
          return Object.entries(contexts).some(([contextKey, isSelected]) => {
            if (!isSelected) return false;

            const variant = data.find(
              (d) =>
                d.SubStepTitle === step &&
                d.CascadeName === cascadeName &&
                d.Context === contextKey,
            );
            if (!variant) return false;

            return (
              (String(variant.Cascaded).toUpperCase() === "TRUE" ||
                variant.Cascaded === true) &&
              (variant.Cascades === "-" || variant.Cascades === "-")
            );
          });
        },
      );

      if (lastCascadedIsSelected) return true;

      return false;
    }
  }

  function fireCameraAnimationForStep(step) {
    if (groupedData[step])
      fireCameraAnimation(
        Object.entries(Object.entries(groupedData[step])[0])[1][1][0]
          .CameraPosition,
        seatCode,
      );
  }

  function isOptionContextOpen(option, openCascadeState = openCascade) {
    if (String(option.Cascaded).toUpperCase() !== "TRUE") return true;

    return openCascadeState.some((item) =>
      String(Object.values(item)[0]).split("|").includes(option.Context),
    );
  }

  function getSelectedContextValue(
    subStepTitle,
    context,
    selectionOverrides = [],
  ) {
    const override = selectionOverrides.find(
      (selectionOverride) =>
        selectionOverride.subStepTitle === subStepTitle &&
        selectionOverride.context === context,
    );
    if (override) return override.value;

    const subStepSelection = dataSelection?.[subStepTitle];
    if (!subStepSelection) return undefined;

    for (const cascadeSelection of Object.values(subStepSelection)) {
      if (cascadeSelection?.[context]) return cascadeSelection[context];
    }

    return undefined;
  }

  function normalizeVariantValue(value) {
    if (!value || value === "-") return value;
    return value.includes("_#") ? value.split("_#")[0] : value;
  }

  function parseConditionalPrerequisite(prerequisite) {
    const match = prerequisite.match(/^<([^:>]+):([^:>]+):(!?[^>]+)>(.+)$/);
    if (!match) return { variantID: prerequisite };

    const [, subStepTitle, context, expectedValue, variantID] = match;
    const isNegated = expectedValue.startsWith("!");

    return {
      variantID,
      condition: {
        subStepTitle,
        context,
        expectedValue: isNegated ? expectedValue.slice(1) : expectedValue,
        isNegated,
      },
    };
  }

  function isPrerequisiteConditionMet(condition, selectionOverrides = []) {
    if (!condition) return true;

    const selectedValue = normalizeVariantValue(
      getSelectedContextValue(
        condition.subStepTitle,
        condition.context,
        selectionOverrides,
      ),
    );
    const matches = selectedValue === condition.expectedValue;

    return condition.isNegated ? !matches : matches;
  }

  function applyPrerequisiteOption(option, variantIDWithColor) {
    changeConfigs(
      option.SubStepTitle,
      option.CascadeName,
      option.Context,
      variantIDWithColor,
    );
    userSelection(option.SubStepTitle, option.CascadeName, option.Context);
  }

  function changePrerequisites(
    prerequisites,
    variantIDWithColor,
    selectionOverrides = [],
  ) {
    let variantIDWithColorSplit = variantIDWithColor.split("_");
    let color =
      variantIDWithColor.indexOf("#") > -1
        ? "_" + variantIDWithColorSplit[variantIDWithColorSplit.length - 1]
        : "";

    if (prerequisites !== "-") {
      let prerequisitesArr = prerequisites.split("|");
      let activeCondition = null;

      for (let index = 0; index < prerequisitesArr.length; index++) {
        const prerequisiteConfig = parseConditionalPrerequisite(
          prerequisitesArr[index],
        );

        if (prerequisiteConfig.condition) {
          activeCondition = prerequisiteConfig.condition;
        }

        if (!isPrerequisiteConditionMet(activeCondition, selectionOverrides))
          continue;

        const prerequisite = prerequisiteConfig.variantID;
        const option = data.filter(
          (option) => option.VariantID === prerequisite,
        )[0];
        // console.log(prerequisite + color);

        if (option) {
          const prerequisiteValue = prerequisite + color;

          if (isOptionContextOpen(option)) {
            applyPrerequisiteOption(option, prerequisiteValue);
          } else {
            setPendingPrerequisites((prev) => {
              const alreadyPending = prev.some(
                (item) => item.variantIDWithColor === prerequisiteValue,
              );
              if (alreadyPending) return prev;
              return [...prev, { variantIDWithColor: prerequisiteValue }];
            });
          }
        }
      }
    }
  }

  function prerequisiteTargetsChangedSubStep(prerequisites, changedOption) {
    if (!prerequisites || prerequisites === "-") return false;

    let activeCondition = null;

    return prerequisites.split("|").some((prerequisite) => {
      const prerequisiteConfig = parseConditionalPrerequisite(prerequisite);

      if (prerequisiteConfig.condition) {
        activeCondition = prerequisiteConfig.condition;
      }

      const conditionMatchesChangedOption =
        activeCondition?.subStepTitle === changedOption.SubStepTitle &&
        activeCondition?.context === changedOption.Context;
      if (!conditionMatchesChangedOption) return false;

      const targetOption = data.find(
        (option) => option.VariantID === prerequisiteConfig.variantID,
      );

      return targetOption?.SubStepTitle === changedOption.SubStepTitle;
    });
  }

  function syncConditionalPrerequisitesForSelectionChange(
    changedOption,
    changedValue,
  ) {
    const selectionOverrides = [
      {
        subStepTitle: changedOption.SubStepTitle,
        context: changedOption.Context,
        value: changedValue,
      },
    ];
    const reappliedSelections = new Set();

    Object.values(dataSelection || {}).forEach((subStepSelection) => {
      Object.values(subStepSelection || {}).forEach((cascadeSelection) => {
        Object.values(cascadeSelection || {}).forEach((selectedValue) => {
          const selectedVariantID = normalizeVariantValue(selectedValue);
          const selectedOption = data.find(
            (option) => option.VariantID === selectedVariantID,
          );

          if (
            !selectedOption ||
            !prerequisiteTargetsChangedSubStep(
              selectedOption.Prerequisites,
              changedOption,
            )
          ) {
            return;
          }

          const selectionKey = selectedOption.VariantID + selectedValue;
          if (reappliedSelections.has(selectionKey)) return;
          reappliedSelections.add(selectionKey);

          changePrerequisites(
            selectedOption.Prerequisites,
            selectedValue,
            selectionOverrides,
          );
        });
      });
    });
  }

  function applyCascade(
    subStepTitle,
    CascadeName,
    triggerContext,
    cascadeContext,
    decascadeContext,
  ) {
    if (cascadeContext !== "-" || decascadeContext !== "-") {
      const cascadeContextArr = cascadeContext.split("|");
      const decascadeContextArr = decascadeContext.split("|");

      setOpenCascade((prev) => {
        // Remove items whose value matches one of the decascadeContextArr elements
        const filtered = prev.filter(
          (item) => !decascadeContextArr.includes(Object.values(item)[0]),
        );

        // Handle decascading (removing deactivated contexts)
        if (filtered) {
          decascadeContextArr.forEach((decascadeContextElement) => {
            // changeConfigs(
            //   subStepTitle,
            //   CascadeName,
            //   decascadeContextElement,
            //   "-",
            //   false
            // );
            userSelection(
              subStepTitle,
              CascadeName,
              decascadeContextElement,
              true,
            );
          });
        }

        const existsInOpenCascade = filtered.find(
          (item) => Object.keys(item)[0] === triggerContext,
        );

        if (existsInOpenCascade) {
          // replace the element’s value with *all* cascade contexts joined back
          return filtered.map((item) =>
            Object.keys(item)[0] === triggerContext
              ? { [triggerContext]: cascadeContextArr.join("|") }
              : item,
          );
        } else {
          // add new entries for each cascadeContext element
          const newEntries = cascadeContextArr.map((ctx) => ({
            [triggerContext]: ctx,
          }));
          return [...filtered, ...newEntries];
        }
      });

      // turn user selected true when there is one option in the cascaded context
      cascadeContextArr.forEach((cascadeContextElement) => {
        let optionsInContext = data.filter(
          (d) => d.Context === cascadeContextElement,
        );
        if (optionsInContext.length === 1)
          userSelection(
            subStepTitle,
            CascadeName,
            cascadeContextElement,
            false,
          );
      });

      // Handle each cascade context for configs & user selection
      // cascadeContextArr.forEach((cascadeContextElement) => {
      //   changeConfigs(
      //     subStepTitle,
      //     CascadeName,
      //     cascadeContextElement,
      //     triggerContext
      //   );
      // userSelection(subStepTitle, CascadeName, cascadeContextElement, false);
      // });
    }
  }

  function handleTabsNavigation(stepType, stps, step, actstp) {
    if (canNavigateToStep(stepType, stps, step, actstp)) {
      if (stepType === "step") {
        setActiveStep(step);
        let newSubSteps = Array.from(
          new Set(
            data
              .filter((item) => item.StepTitle === step)
              .map((item) => item.SubStepTitle),
          ),
        );
        setSubSteps(newSubSteps);
        //
        var activeSubStepIndex = -1;

        // eslint-disable-next-line array-callback-return
        newSubSteps.map((newSubStep) => {
          if (isStepCompleted("substep", newSubStep)) {
            activeSubStepIndex++;
          }
        });
        //
        let actvSbStp =
          steps.indexOf(step) > steps.indexOf(activeStep) ||
          activeSubStepIndex === -1
            ? newSubSteps[0]
            : newSubSteps[activeSubStepIndex];
        //
        setActiveSubStep(actvSbStp);
        fireCameraAnimationForStep(actvSbStp);
      } else {
        setActiveSubStep(step);
        fireCameraAnimationForStep(step);
      }
    }
  }

  function handleColorChangeComplete(VariantID, color) {
    setColorPickerValue((prev) => ({
      ...prev,
      [VariantID]: color.hex,
    }));
  }

  function openColorPicker(option, target) {
    const rect = target.getBoundingClientRect();
    const pickerWidth = option.VariantID.indexOf("collins") > -1 ? 232 : 225;
    const margin = 12;
    const left = Math.min(
      Math.max(rect.left + rect.width / 2 - pickerWidth / 2, margin),
      window.innerWidth - pickerWidth - margin,
    );

    setColorPickerLeft(left);
    setOptionWithColorPicker(option);
  }

  function useOutsidePicker(ref) {
    useEffect(() => {
      function handleClickOutside(event) {
        if (ref.current && !ref.current.contains(event.target)) {
          setOptionWithColorPicker({});
        }
      }

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }

  function onOptionSelection(option, colorOverride) {
    if (Object.keys(option).length === 0) return;

    let colorPickerValueOfOption =
      colorOverride || colorPickerValue[option.VariantID];
    const isColorOption =
      option.VariantID.indexOf("custom") > -1 &&
      option.VariantID.indexOf("color") > -1;
    const isCollinsColorOption =
      option.VariantID.indexOf("collins") > -1 &&
      option.VariantID.indexOf("color") > -1;
    if ((isColorOption || isCollinsColorOption) && !colorPickerValueOfOption) {
      setOptionWithColorPicker({});
      return;
    }

    let variantIDWithColor =
      option.VariantID +
      (colorPickerValueOfOption ? "_" + colorPickerValueOfOption : "");
    changeConfigs(
      activeSubStep,
      option.CascadeName,
      option.Context,
      variantIDWithColor,
    );
    userSelection(activeSubStep, option.CascadeName, option.Context, false);
    fireCameraAnimation(
      groupedData[activeSubStep][option.Context][0]?.CameraPosition,
      seatCode,
    );
    setTimeout(() => {
      saveUserConfiguration(
        authUser?.id_token,
        "",
        "",
        "",
        dataSelection,
        {},
        null,
        customFiles,
        false,
        selectedByUser,
      );
    }, 0);
    changePrerequisites(option.Prerequisites, variantIDWithColor);
    syncConditionalPrerequisitesForSelectionChange(option, variantIDWithColor);
    applyCascade(
      option.SubStepTitle,
      option.CascadeName,
      option.Context,
      option.Cascades,
      option.Decascades,
    );
    setExpanded(true);
    setOptionWithColorPicker({});
  }

  function colorConfirmButton(type, variantID) {
    const isColorSelected = colorPickerValue[variantID];
    return (
      <Box
        sx={{
          height: "29px",
          width: type === "chrome" ? "195px" : "199px",
          marginLeft: "15px",
          border: isNight ? "1px solid #d8deea" : "1px solid #464647",
          pointerEvents: isColorSelected ? "auto" : "none",
          cursor: isColorSelected ? "pointer" : "auto",
          opacity: isColorSelected ? "1" : "0.4",
          transition: "opacity 0.1s ease, border-color 0.35s ease",
        }}
        onMouseDown={(event) => {
          event.stopPropagation();
        }}
        onClick={() => {
          onOptionSelection(optionWithColorPicker, colorPickerValue[variantID]);
        }}
      >
        <span
          style={{
            color: isNight ? nightTextColor : "#464647",
            fontSize: "calc(0.6225rem + var(--large-screen-font-bump))",
            fontWeight: "500",
            display: "inline-block",
            transition: "color 0.35s ease",
          }}
        >
          CONFIRM
        </span>
        <Check
          style={{
            color: isNight ? nightTextColor : "#464647",
            width: "13px",
            height: "auto",
            verticalAlign: "middle",
            paddingLeft: "3px",
            display: "inline-block",
            transition: "color 0.35s ease",
          }}
        ></Check>
      </Box>
    );
  }

  function sharedColorPicker() {
    const isCustomColor =
      optionWithColorPicker.VariantID?.indexOf("custom") > -1 &&
      optionWithColorPicker.VariantID?.indexOf("color") > -1;
    const isCollinsColor =
      optionWithColorPicker.VariantID?.indexOf("collins") > -1 &&
      optionWithColorPicker.VariantID?.indexOf("color") > -1;

    return (
      <>
        <div
          className="color-picker-popover"
          ref={isCustomColor ? customPickerRef : null}
          style={{
            position: "fixed",
            bottom: "125px",
            left: colorPickerLeft ?? 0,
            opacity: isCustomColor ? 1 : 0,
            pointerEvents: isCustomColor ? "auto" : "none",
            transition:
              "opacity 0.1s ease, background-color 0.35s ease, box-shadow 0.35s ease",
            zIndex: 999999,
            background: isNight ? "#566176" : "white",
            boxShadow: isNight
              ? "rgba(0, 0, 0, 0.25) 0px 0px 50px 0px"
              : "rgba(0, 0, 0, 0.1) 0px 0px 50px 0px",
            paddingBottom: "15px",
          }}
        >
          <ChromePicker
            onChange={(color) => {
              handleColorChangeComplete(optionWithColorPicker.VariantID, color);
            }}
            onChangeComplete={(color) => {
              handleColorChangeComplete(optionWithColorPicker.VariantID, color);
            }}
            color={colorPickerValue[optionWithColorPicker.VariantID] ?? "#fff"}
          />
          {colorConfirmButton("chrome", optionWithColorPicker.VariantID)}
        </div>

        <div
          className="color-picker-popover"
          ref={isCollinsColor ? collinsPickerRef : null}
          style={{
            position: "fixed",
            bottom: "125px",
            left: colorPickerLeft ?? 0,
            opacity: isCollinsColor ? 1 : 0,
            pointerEvents: isCollinsColor ? "auto" : "none",
            transition:
              "opacity 0.1s ease, background-color 0.35s ease, box-shadow 0.35s ease",
            zIndex: 999999,
            background: isNight ? "#566176" : "white",
            boxShadow: isNight
              ? "rgba(0, 0, 0, 0.25) 0px 0px 50px 0px"
              : "rgba(0, 0, 0, 0.1) 0px 0px 50px 0px",
            paddingBottom: "15px",
          }}
        >
          <TwitterPicker
            onChangeComplete={(color) => {
              handleColorChangeComplete(optionWithColorPicker.VariantID, color);
            }}
            colors={[
              "#da291c",
              "#fa4616",
              "#9d2235",
              "#76232f",
              "#e5d603",
              "#672146",
              "#503938",
              "#645c59",
              "#6e5e52",
              "#8b8075",
              "#7d7773",
              "#a29b96",
              "#a99c95",
              "#bbafa9",
              "#dbd5cd",
              "#d9d9d6",
              "#d57800",
              "#dbd5cd",
              "#d3bc8d",
              "#0050b5",
              "#00205b",
              "#005670",
              "#253746",
              "#434f69",
              "#676c73",
              "#78838e",
              "#53565a",
              "#898d8d",
              "#a7a8a9",
              "#acb3b8",
              "#003a70",
              "#a6192e",
              "#051c2c",
              "#0072ce",
              "#425563",
              "#a2aaad",
              "#2d2926",
              "#ba0c2f",
              "#e4002b",
              "#fcd299",
              "#0032a0",
            ]}
            color={colorPickerValue[optionWithColorPicker.VariantID] ?? "#fff"}
            triangle={"hide"}
            width="232px"
          />
          {colorConfirmButton("twitter", optionWithColorPicker.VariantID)}
        </div>
      </>
    );
  }

  function shortContext(context) {
    let shortFormAppliesFor = [
      "Perrone",
      "Muirhead",
      "Leather Cover",
      "Fabric Cover",
      "Faux Leather",
      "Tapis",
      "Decorative Stitch",
      "Cover Style",
      "Standard Cover",
      "Custom Cover",
      "Custom Embossing",
      "Mono Primary Color",
      "Dual Primary Color",
      "Dual Secondary Color",
      "Lining Color",
      "Laminated Cover",
    ];
    let removeWords = ["Dual", "Mono", "Back", "Bottom", "Headrest"];
    let context_ = context;
    shortFormAppliesFor.forEach((shortForm) => {
      if (context_.indexOf(shortForm) > -1) {
        context_ = shortForm;
      }
    });
    removeWords.forEach((wordToRemove) => {
      if (context_.indexOf(wordToRemove) > -1) {
        context_ = context_.replaceAll(wordToRemove + " ", "");
      }
    });
    return context_;
  }

  const groupedByStep = subSteps?.reduce((acc, step) => {
    acc[step] = data.filter((item) => item.SubStepTitle === step);
    return acc;
  }, {});

  const getContextsForStep = (step) => {
    const contexts = groupedByStep?.[step]?.map((item) => item.Context);
    return Array.from(new Set(contexts));
  };

  const getSubStepDivision = (option) => {
    const division = option?.subStepDivision?.trim();
    return division && division !== "-" ? division : "";
  };

  const renderOption = (option) => {
    // const isColor = false; // option.Image?.startsWith("#");
    const background = isNight ? "#848ba1" : "#c7c7c7";
    //
    let colorPickerValueOfOption = colorPickerValue[option.VariantID];
    const optionSelectionValue =
      dataSelection[option.SubStepTitle][option.CascadeName][option.Context];

    const isOptionSelected =
      optionSelectionValue ===
        option.VariantID +
          (colorPickerValueOfOption ? "_" + colorPickerValueOfOption : "") ||
      optionSelectionValue?.startsWith(option.VariantID + "_#");
    const isOptionSelectedByUser =
      selectedByUser[activeSubStep][option.CascadeName][option.Context];
    //
    const isCustomColor =
      option.VariantID.indexOf("custom") > -1 &&
      option.VariantID.indexOf("color") > -1;

    const isCollinsColor =
      option.VariantID.indexOf("collins") > -1 &&
      option.VariantID.indexOf("color") > -1;

    const isCustomDesign =
      option.VariantID.indexOf("custom") > -1 &&
      option.VariantID.indexOf("color") === -1;

    const optionSelectedColor =
      optionSelectionValue?.indexOf("#") > -1 &&
      optionSelectionValue?.indexOf(option.VariantID) > -1
        ? "#" + optionSelectionValue?.split("#")[1]
        : background;
    let optionCustomFile = customFiles.find(
      (customFile) => option.VariantID === Object.keys(customFile)[0],
    );
    if (optionCustomFile)
      optionCustomFile = optionCustomFile[Object.keys(optionCustomFile)[0]];

    if (optionCustomFile) optionCustomFile = addDataUrlPrefix(optionCustomFile);

    let noneIconBg =
      option.VariantID.indexOf("_no") > -1 ||
      option.VariantID.indexOf("_none") > -1
        ? "url(" + iconsPath + "/none.png),"
        : "";

    return option.Hidden === "FALSE" ? (
      <Grid key={option.VariantID}>
        <>
          {isCustomDesign && (
            <UploadFileModal
              optionVariantID={option.VariantID}
              addToCustomFiles={addToCustomFiles}
              isModalOpen={uploadModalOpenId === option.VariantID}
              isNight={isNight}
              onModalClose={(previewUrl) => {
                setUploadModalOpenId("");
                // if (previewUrl && previewUrl?.toString().indexOf("http") > -1) {
                onOptionSelection(option);
                // }
              }}
            />
          )}

          <Box
            display="flex"
            flexDirection="column"
            alignItems="left"
            sx={{ width: 40 }}
          >
            <Box position={"relative"}>
              <Tooltip
                enterDelay={400}
                leaveDelay={100}
                placement={"bottom"}
                title={
                  option.DisabledMsg === "-" ? (
                    option.Text
                  ) : (
                    <div style={{ textAlign: "center" }}>
                      {option.Text}
                      <br></br>
                      {option.DisabledMsg}
                    </div>
                  )
                }
              >
                {/* <Tooltip title={option.Text} placement="bottom" enterDelay={0}> */}
                <Paper
                  onClick={(event) => {
                    if (isCustomColor) {
                      openColorPicker(option, event.currentTarget);
                    } else if (isCollinsColor) {
                      openColorPicker(option, event.currentTarget);
                    } else if (isCustomDesign) {
                      setUploadModalOpenId(option.VariantID);
                    } else if (option.Disabled !== "TRUE") {
                      onOptionSelection(option);
                    }
                  }}
                  elevation={0}
                  sx={{
                    width: 40,
                    height: 40,
                    background: optionSelectedColor,
                    backgroundImage:
                      isOptionSelected && optionCustomFile
                        ? "url(" + optionCustomFile + ")"
                        : noneIconBg +
                          "url(" +
                          iconsPath +
                          option.Image.replaceAll(" ", "%20") +
                          ")",

                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    borderRadius: "0px",
                    mb: 0.5,
                    cursor: option.Disabled !== "TRUE" ? "pointer" : "no-drop",
                  }}
                />
              </Tooltip>

              <Box
                sx={{
                  pointerEvents: "none",
                  transition: "background-color 0.15s ease",
                }}
                position={"absolute"}
                top={"4.5px"}
                right={"3px"}
                height={"12px"}
                width={"12px"}
                borderRadius={"100px"}
                pointerEvents={"none"}
                backgroundColor={
                  isOptionSelected
                    ? isOptionSelectedByUser || option.Disabled === "TRUE"
                      ? "#CE1126"
                      : // ? option.Disabled !== "TRUE"
                        //   ? "#CE1126"
                        //   : "#bcbcbcff"
                        "#bcbcbcff"
                    : "transparent"
                }
              >
                <Box marginTop={"-6.5px"}>
                  <Check
                    size={10}
                    color={isOptionSelected ? "white" : "transparent"}
                  ></Check>
                </Box>
              </Box>
            </Box>

            <Typography
              variant="body2"
              textTransform={"upperCase"}
              fontSize={"0.35rem"}
              align="center"
              color={isNight ? nightTextColor : "#000"}
              sx={{
                maxWidth: 60,
                height: "20.13px",
                cursor: "pointer",
                transition: "color 0.35s ease",
              }}
            >
              {option.Text}
            </Typography>
          </Box>
        </>
      </Grid>
    ) : null;
  };

  const renderStepContent = (step) => {
    const contexts = getContextsForStep(step);
    let currentOpts = data.filter((opt) => opt.SubStepTitle === activeSubStep);
    let excludeProduct =
      productBodyType === "narrowbody" ? "widebody" : "narrowbody";
    currentOpts = currentOpts.filter(
      (opt) =>
        opt.VariantID.indexOf(excludeProduct) === -1 &&
        opt.Cascaded === "FALSE",
    );

    // eslint-disable-next-line array-callback-return
    openCascade.map((op) => {
      let existsInOpenCascade = data.filter((opt) => {
        let opKeys = Object.keys(op);

        return opt.Context === op[opKeys[0]];
      });

      if (
        existsInOpenCascade &&
        currentOpts &&
        existsInOpenCascade[0] &&
        currentOpts[0] &&
        existsInOpenCascade[0].SubStepTitle === currentOpts[0].SubStepTitle
      ) {
        currentOpts = [...currentOpts, ...existsInOpenCascade];
      }
    });

    const contextSections = contexts
      .map((context) => {
        let options = groupedByStep[step].filter(
          (opt) => opt.Context === context,
        );

        if (
          options[0]?.VariantID.indexOf("narrowbody") > -1 ||
          options[0]?.VariantID.indexOf("widebody") > -1
        )
          options = options.filter(
            (opt) => opt.VariantID.indexOf(productBodyType) > -1,
          );

        const hasVisibleOptions = options.some(
          (option) => String(option.Hidden).toUpperCase() !== "TRUE",
        );

        const existsInOpenCascade = openCascade.find(
          (item) => Object.values(item)[0] === context,
        );

        const isVisible =
          hasVisibleOptions &&
          (options[0]?.Cascaded === "FALSE" || existsInOpenCascade);

        return {
          context,
          options,
          existsInOpenCascade,
          division: getSubStepDivision(options[0]),
          isVisible,
        };
      })
      .filter((section) => section.isVisible);

    const divisions = Array.from(
      new Set(
        contextSections
          .map((section) => section.division)
          .filter((division) => division),
      ),
    );
    const hasDivisions = divisions.length > 0;
    const activeDivision = activeSubStepDivisions[step] || divisions[0];
    const activeDivisionIndex = Math.max(divisions.indexOf(activeDivision), 0);
    const divisionKey = (division) => `${step}|${division}`;
    const activeDivisionWidth =
      divisionPanelWidths[divisionKey(activeDivision)];
    const activeDivisionOffset = divisions
      .slice(0, activeDivisionIndex)
      .reduce(
        (offset, division) =>
          offset + (divisionPanelWidths[divisionKey(division)] || 0),
        0,
      );

    const renderContextSection = ({ context, options }, index) => (
      <Grid key={context || index} sx={{ transition: "0.5s all" }}>
        <Box>
          {/* minWidth={150} */}
          <Grid
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant="body2"
              textTransform={"upperCase"}
              fontSize={"calc(0.61rem + var(--large-screen-font-bump))"}
              align={"left"}
              fontWeight={"bold"}
              marginBottom={"6px"}
              gutterBottom
              color={isNight ? nightTextColor : "inherit"}
              sx={{ transition: "color 0.35s ease" }}
            >
              {shortContext(context)}
            </Typography>
            {options[0]?.GlobalApplyMsg !== "-" ? (
              <Tooltip placement="top" title={options[0]?.GlobalApplyMsg}>
                <Info
                  style={{
                    width: "14px",
                    height: "14px",
                    marginLeft: "5px",
                    color: isNight ? nightMutedTextColor : "inherit",
                    transition: "color 0.35s ease",
                  }}
                />
              </Tooltip>
            ) : (
              <></>
            )}
          </Grid>
          <Grid
            container
            spacing={
              // xs: 0.7,
              // lg: 1.5,
              // xl: 2,
              currentOpts.length >= 16 ? 1 : 1.5
            }
          >
            {options.map(renderOption)}
          </Grid>
        </Box>
      </Grid>
    );

    const renderCollapsedDivision = (division) => {
      const DivisionArrow =
        divisions.indexOf(division) === 0
          ? KeyboardArrowLeftOutlined
          : KeyboardArrowRightOutlined;

      return (
        <Box
          key={division}
          onClick={() =>
            setActiveSubStepDivisions((prev) => ({
              ...prev,
              [step]: division,
            }))
          }
          sx={{
            alignSelf: "stretch",
            borderLeft: isNight ? "1px solid #7b8498" : "1px solid #d4d4d4",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            minHeight: "75px",
            overflow: "hidden",
            px: 1.5,
            color: isNight ? nightMutedTextColor : dayPrimaryTextColor,
            transition: "color 0.35s ease, border-color 0.35s ease",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          <DivisionArrow
            sx={{
              fontSize: "calc(1.06rem + var(--large-screen-font-bump))",
              mr: 0.5,
              color: isNight ? nightMutedTextColor : "#CE1126",
            }}
          />
          <Typography
            variant="body2"
            textTransform={"uppercase"}
            fontSize={"calc(0.61rem + var(--large-screen-font-bump))"}
            fontWeight={"bold"}
          >
            {division}
          </Typography>
        </Box>
      );
    };

    const renderDivisionPanel = (division) => (
      <Box
        key={division}
        ref={(node) => {
          divisionPanelRefs.current[divisionKey(division)] = node;
        }}
        sx={{
          display: "flex",
          gap: currentOpts.length >= 16 ? "1.7vw" : "3vw",
          flexShrink: 0,
        }}
      >
        {contextSections
          .filter((section) => section.division === division)
          .map(renderContextSection)}
      </Box>
    );

    const renderDividedSections = () => (
      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          gap: currentOpts.length >= 16 ? "1.7vw" : "3vw",
          overflow: "hidden",
          minWidth: 0,
        }}
      >
        {divisions.slice(0, activeDivisionIndex).map(renderCollapsedDivision)}
        <Box
          sx={{
            overflow: "hidden",
            flexShrink: 0,
            minWidth: 0,
            width: activeDivisionWidth
              ? `${activeDivisionWidth}px`
              : "fit-content",
            transition: "width 0.35s ease",
          }}
        >
          <Box
            sx={{
              display: "flex",
              transform: `translateX(-${activeDivisionOffset}px)`,
              transition: activeDivisionWidth ? "transform 0.35s ease" : "none",
              willChange: "transform",
            }}
          >
            {divisions.map(renderDivisionPanel)}
          </Box>
        </Box>
        {divisions.slice(activeDivisionIndex + 1).map(renderCollapsedDivision)}
      </Box>
    );

    const renderUndividedSections = () => (
      <>{contextSections.map(renderContextSection)}</>
    );

    return (
      <Grid
        container
        spacing={
          // currentOpts.length >= 16 ? 4 : currentOpts.length >= 14 ? 6 : 7
          currentOpts.length >= 16
            ? "1.7vw"
            : currentOpts.length >= 14
              ? "2vw"
              : "3vw"
          // {
          // xs: 0.5,
          // sm: 1,
          // sm_md: 1,
          // md: 1.5,
          // md_lg: 3.5,
          // lg: 5,
          // lg_xl_1: 6,
          // lg_xl_2: 7,
          // xl: 8,
          // }
        }
        wrap="nowrap"
        sx={{ overflowX: "auto", transition: "0.1s all" }}
      >
        {hasDivisions ? renderDividedSections() : renderUndividedSections()}
      </Grid>
    );
  };

  function canNavigateToStep(stepType, stps, stp, activeStp) {
    const stepIndex = stps.indexOf(stp);
    const activeIndex = stps.indexOf(activeStp);

    return (
      // Always allow navigating to the first step
      stepIndex === 0 ||
      // Allow if this is the current active step
      stp === activeStp ||
      // Allow navigation only if *all previous steps* are completed
      stps.slice(0, stepIndex).every((stp) => isStepCompleted(stepType, stp)) ||
      // Allow moving to the immediate next step if the current one is completed
      (isStepCompleted(stepType, activeStp) && stepIndex === activeIndex + 1)
    );
  }

  function number(index) {
    if (index + 1 === 1)
      return <LooksOneOutlined sx={{ width: "19px", height: "1.0rem" }} />;
    if (index + 1 === 2)
      return <LooksTwoOutlined sx={{ width: "19px", height: "1.0rem" }} />;
    if (index + 1 === 3)
      return <Looks3Outlined sx={{ width: "19px", height: "1.0rem" }} />;
    if (index + 1 === 4)
      return <Looks4Outlined sx={{ width: "19px", height: "1.0rem" }} />;
    if (index + 1 === 5)
      return <Looks5Outlined sx={{ width: "19px", height: "1.0rem" }} />;
  }

  function stepsTabs() {
    // let actstp = stepType === "step" ? activeStep : activeSubStep;
    // let stps = stepType === "step" ? steps : subSteps;

    if (steps)
      return (
        <div style={{ display: "flex", width: "100%", marginBottom: "1.25vh" }}>
          {steps?.map((step, index) => {
            let subStepsOfStep = getSubSteps(step);
            let completedSubSteps = 0;
            // eslint-disable-next-line array-callback-return
            subStepsOfStep.map((sbstp) => {
              if (isStepCompleted("substep", sbstp))
                completedSubSteps = completedSubSteps + 1;
            });

            return (
              <Box
                key={index}
                sx={{
                  flex: expanded ? 0 : 1,
                  mx: expanded ? 0 : 0.5,
                  transition: "0.5s all",
                }}
              >
                <Tooltip
                  enterDelay={1000}
                  leaveDelay={100}
                  placement="top"
                  title={
                    !canNavigateToStep("step", steps, step, activeStep)
                      ? "Compelete the currect step to move to the next"
                      : ""
                  }
                >
                  <Box
                    key={step}
                    onClick={() =>
                      handleTabsNavigation("step", steps, step, activeStep)
                    }
                  >
                    <Box
                      sx={{
                        height: "20px",
                        width:
                          expanded && step === activeStep
                            ? "calc(100vw - 12vh)"
                            : expanded && step !== activeStep
                              ? "0px"
                              : "calc(" +
                                100 / steps.length +
                                "vw - " +
                                (120 * steps.length) / step.length +
                                "px)",
                        overflow: "hidden",
                        transition: "0.3s all",
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontSize={{
                          xs: "calc(0.56rem + var(--large-screen-font-bump))",
                          md_lg:
                            "calc(0.61rem + var(--large-screen-font-bump))",
                          lg: "calc(0.68rem + var(--large-screen-font-bump))",
                        }}
                        fontWeight={activeStep === step ? 600 : 400}
                        textAlign={expanded ? "left" : "center"}
                        color={
                          isNight
                            ? activeStep === step
                              ? nightTextColor
                              : nightMutedTextColor
                            : activeStep === step
                              ? dayPrimaryTextColor
                              : dayMutedTextColor
                        }
                        sx={{ transition: "color 0.35s ease" }}
                      >
                        {number(index)}{" "}
                        <span style={{ verticalAlign: "super" }}>{step}</span>
                      </Typography>
                    </Box>
                  </Box>
                </Tooltip>
                <Box sx={{ height: "7px" }}></Box>
                <LinearProgress
                  // backgroundColor={"#CE1126"}
                  color={"#CE1126"}
                  key={index}
                  variant="determinate"
                  value={(completedSubSteps / subStepsOfStep.length) * 100}
                  sx={{
                    width:
                      expanded && step === activeStep
                        ? "calc(100vw - 12vh)"
                        : expanded && step !== activeStep
                          ? "0px"
                          : "calc(" +
                            100 / steps.length +
                            "vw - " +
                            (12 * steps.length) / step.length +
                            "vh)",
                    transition: "0.35s all",
                    // transitionDelay: "0.1s",
                    height: 6,
                    borderRadius: 5,
                    backgroundColor: isNight ? "#7b818f" : "#e1e1e1",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "#CE1126",
                    },
                  }}
                />
              </Box>
            );
          })}
        </div>
      );

    // return (
    //   <>
    //     <Box
    //       display="flex"
    //       width={stepType === "step" ? "100%" : "70%"}
    //       mb={2}
    //       marginBottom={"12px"}
    //     >
    //       {stps?.map((step, index) => (
    //         <Tooltip
    //           enterDelay={1000}
    //           leaveDelay={100}
    //           placement="top"
    //           title={
    //             !canNavigateToStep(stepType, stps, step, actstp)
    //               ? "Compelete the currect step to move to the next"
    //               : ""
    //           }
    //         >
    //           <Box
    //             key={step}
    //             onClick={() => {
    //               if (canNavigateToStep(stepType, stps, step, actstp)) {
    //                 if (stepType === "step") {
    //                   setActiveStep(step);
    //                   let newSubSteps = Array.from(
    //                     new Set(
    //                       data
    //                         .filter((item) => item.StepTitle === step)
    //                         .map((item) => item.SubStepTitle)
    //                     )
    //                   );
    //                   setSubSteps(newSubSteps);
    //                   setActiveSubStep(
    //                     newSubSteps[
    //                       steps.indexOf(step) > steps.indexOf(activeStep)
    //                         ? 0
    //                         : newSubSteps.length - 1
    //                     ]
    //                   );
    //                   fireCameraAnimationForStep(newSubSteps[0]);
    //                 } else {
    //                   setActiveSubStep(step);
    //                   fireCameraAnimationForStep(step);
    //                 }
    //               }
    //             }}
    //             sx={{
    //               flex: 1,
    //               textAlign: "center",
    //               cursor: canNavigateToStep(stepType, stps, step, actstp)
    //                 ? "pointer"
    //                 : "default",
    //               border: stepType === "substep" ? "3px solid" : "none",
    //               borderBottom: stepType === "step" ? "5px solid" : "3px solid",
    //               borderColor:
    //                 step === activeSubStep || stepType === "step"
    //                   ? isStepCompleted(stepType, step)
    //                     ? "#CE1126" // completed
    //                     : index === stps.indexOf(actstp)
    //                     ? "#63666a" // active
    //                     : "#4f4f4f"
    //                   : "transparent", // upcoming
    //               padding: "10px",
    //               marginRight:
    //                 steps.indexOf(step) === steps.length - 1 ? "0px" : "7px",
    //             }}
    //           >
    //             <Typography
    //               variant="body2"
    //               fontSize={{
    //                 xs: "0.5rem",
    //                 md_lg: "0.55rem",
    //                 lg: "0.62rem",
    //               }}
    //               fontWeight={actstp === step ? 600 : 400}
    //               color={
    //                 isStepCompleted(stepType, step)
    //                   ? actstp === step
    //                     ? "#CE1126"
    //                     : "#d9919aff"
    //                   : actstp === step
    //                   ? "#63666a"
    //                   : "#4f4f4f"
    //               }
    //               sx={{ transition: "color 0.35s ease" }}
    //             >
    //               {step}
    //             </Typography>

    //             {/* <Box
    //               key={step}
    //               flex={1}
    //               sx={{
    //                 marginRight: index !== stps.length - 1 ? "7px" : "0px",
    //               }}
    //             >
    //               <Box
    //                 height={stepType === "step" ? 5 : 3}
    //                 sx={{
    //                   backgroundColor: isStepCompleted(stepType, step)
    //                     ? "#CE1126" // completed
    //                     : index === stps.indexOf(actstp)
    //                     ? "#63666a" // active
    //                     : "#4f4f4f", // upcoming
    //                   transition: "background-color 0.35s ease",
    //                   borderRadius: "2px !important",
    //                   margin: "auto",
    //                   marginTop: "10px",
    //                   width: stepType === "step" ? "100%" : "150px",
    //                 }}
    //               />
    //             </Box> */}
    //           </Box>
    //         </Tooltip>
    //       ))}
    //     </Box>

    //     {/* <Box display="flex" width={"100%"} mb={3} marginBottom={"12px"}>
    //       {stps?.map((step, index) => {
    //         return <></>;
    //       })}
    //     </Box> */}
    //   </>
    // );
  }

  function subStepsTabs() {
    return (
      <Box sx={{ position: "relative", height: "calc(33px + 3vh)" }}>
        <Box
          sx={{ position: "absolute", width: subSteps.length * 12.5 + "vw" }}
        >
          <Box
            sx={{
              transition: "0.3s all",
              left: subSteps.indexOf(activeSubStep) * 12.5 + "vw",
              position: "absolute",
              height: "39px",
              width: "12.5vw",
              border: "3px solid #CE1126",
              boxSizing: "border-box",
            }}
          ></Box>
        </Box>
        <Box
          position="absolute"
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
          width={subSteps.length * 12.5 + "vw"}
          mb={2}
          textAlign="left"
        >
          {subSteps?.map((subStep, index) => (
            <Tooltip
              key={index}
              enterDelay={1000}
              leaveDelay={100}
              placement={"top"}
              title={
                !canNavigateToStep("substep", subSteps, subStep, activeSubStep)
                  ? "Compelete the currect step to move to the next"
                  : ""
              }
            >
              <Box
                key={index + subStep}
                // border="3px solid blue"
                boxSizing="border-box"
                onClick={() => {
                  if (
                    canNavigateToStep(
                      "substep",
                      subSteps,
                      subStep,
                      activeSubStep,
                    )
                  ) {
                    // if (stepType === "step") {
                    //   setActiveStep(subStep);
                    //   let newSubSteps = Array.from(
                    //     new Set(
                    //       data
                    //         .filter((item) => item.StepTitle === subStep)
                    //         .map((item) => item.SubStepTitle)
                    //     )
                    //   );
                    //   setSubSteps(newSubSteps);
                    //   setActiveSubStep(
                    //     newSubSteps[
                    //       steps.indexOf(subStep) > steps.indexOf(activeStep)
                    //         ? 0
                    //         : newSubSteps.length - 1
                    //     ]
                    //   );
                    //   fireCameraAnimationForStep(newSubSteps[0]);
                    // }
                    // else {
                    setActiveSubStep(subStep);
                    fireCameraAnimationForStep(subStep);
                    // }
                  }
                }}
                sx={{
                  width: "12.5vw",
                  textAlign: "center",
                  cursor: canNavigateToStep(
                    "substep",
                    subSteps,
                    subStep,
                    activeSubStep,
                  )
                    ? "pointer"
                    : "default",
                  padding: "13.5px 0",
                  margin: "auto",
                  // marginRight:
                  //   steps.indexOf(subStep) === steps.length - 1 ? "0px" : "7px",
                }}
              >
                <Typography
                  component="div"
                  variant="body2"
                  fontSize={"calc(0.61rem + var(--large-screen-font-bump))"}
                  textTransform={"uppercase"}
                  // fontSize={{
                  //   xs: "0.5rem",
                  //   md_lg: "0.55rem",
                  //   lg: "0.62rem",
                  // }}
                  fontWeight={600}
                  // fontWeight={activeSubStep === subStep ? 600 : 400}
                  color={
                    isNight
                      ? activeSubStep === subStep
                        ? nightTextColor
                        : nightMutedTextColor
                      : activeSubStep === subStep
                        ? "#CE1126"
                        : dayPrimaryTextColor
                  }
                  sx={{
                    transition: "color 0.35s ease",
                    width: "fit-content",
                    margin: "auto",
                    position: "relative",
                  }}
                >
                  {/* <Check
                    style={{
                      width: "17px",
                      height: "0.9rem",
                      color:
                        isStepCompleted("substep", subStep) &&
                        subStep !== activeSubStep
                          ? "inherit"
                          : "transparent",
                    }}
                  ></Check> */}

                  <div
                    style={{
                      verticalAlign: "top",
                      width: "fit-content",
                      margin: "auto",
                      position: "relative",
                    }}
                  >
                    <Check
                      style={{
                        position: "absolute",
                        left: "-20px",
                        width: "17px",
                        height: "0.9rem",
                        color:
                          isStepCompleted("substep", subStep) &&
                          subStep !== activeSubStep
                            ? isNight
                              ? nightMutedTextColor
                              : "inherit"
                            : "transparent",
                        transition: "color 0.35s ease",
                      }}
                    ></Check>
                    <div>{subStep}</div>
                  </div>
                </Typography>
              </Box>
            </Tooltip>
          ))}
        </Box>

        {/* <Box
          display="flex"
          mb={2}
          marginBottom={"2.3vh"}
          position={"absolute"}
          right={"0"}
          top={"7px"}
        >
          <Box
            sx={{
              position: "relative",
              marginRight: "10px",
              height: "14px",
              width: "14px",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                border: isStepCompleted("step", activeStep)
                  ? "2px solid #464647ff"
                  : "2px solid #4f4f4f",
                borderRadius: "3px",
                height: "10px",
                width: "10px",
                transition: "borderColor 0.35s ease",
                top: "1px",
              }}
            ></Box>
            <Box
              sx={{
                marginLeft: "1px",
                marginTop: "-5px",
              }}
            >
              <Check
                size={10}
                color={
                  isStepCompleted("step", activeStep) ? "#464647ff" : "#4f4f4f"
                }
                sx={{ transition: "color 0.35s ease" }}
              ></Check>
            </Box>
          </Box>

          <Box>
            <Typography
              variant="body2"
              fontSize={"calc(0.62rem + var(--large-screen-font-bump))"}
              letterSpacing={"0.5px"}
              lineHeight={2}
              color={isStepCompleted("step", activeStep) ? "#000" : "#4f4f4f"}
              sx={{ transition: "color 0.35s ease" }}
            >
              {steps.indexOf(activeStep) + 1} OUT OF {steps.length}{" "}
              {isStepCompleted("step", activeStep)
                ? "IS COMPLETED"
                : "TO COMPLETE"}
            </Typography>
          </Box>
        </Box> */}
      </Box>
    );
  }

  return (
    <Box
      position={"absolute"}
      bottom={"0"}
      width={"100%"}
      sx={{
        color: isNight ? nightTextColor : "inherit",
        transition: "color 0.35s ease",
      }}
    >
      {sharedColorPicker()}
      <AnimatePresence>
        {isOpen && (
          <MotionBox
            key="configurator-content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            overflow="hidden"
          >
            <div
              id="options-container"
              // style={{
              //   position: "absolute",
              //   bottom: "0",
              //   width: "100%",
              // }}
            >
              <Box width="calc(100% - 6vh)" sx={{ margin: "2vh 3vh" }}>
                {/* Tabs */}
                <Box
                  sx={{
                    position: "relative",
                    height: "calc(30px + 2.5vh)",
                    cursor: "pointer",
                  }}
                >
                  <Box sx={{ position: "absolute" }}>{stepsTabs()}</Box>
                  <Box
                    sx={{
                      position: "absolute",
                      right: 0,
                      top: -1,
                      opacity: expanded ? 1 : 0,
                      pointerEvents: expanded ? "auto" : "none",
                      transition: "0.3s all",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    {steps?.map((step, index) => (
                      <div
                        key={index}
                        style={{
                          verticalAlign: "top",
                          marginTop: "-1.5px",
                          color: canNavigateToStep(
                            "step",
                            steps,
                            step,
                            activeStep,
                          )
                            ? isNight
                              ? nightTextColor
                              : dayPrimaryTextColor
                            : isNight
                              ? nightMutedTextColor
                              : dayMutedTextColor,
                          transition: "color 0.35s ease",
                        }}
                        onClick={() => {
                          handleTabsNavigation("step", steps, step, activeStep);
                          setExpanded(true);
                        }}
                      >
                        <Tooltip
                          enterDelay={100}
                          leaveDelay={100}
                          placement="top"
                          title={
                            canNavigateToStep("step", steps, step, activeStep)
                              ? step
                              : "Compelete the currect step to move to the next"
                          }
                        >
                          {number(index)}
                        </Tooltip>
                      </div>
                    ))}
                    <div onClick={() => setExpanded(false)}>
                      <span
                        style={{
                          verticalAlign: "top",
                          fontSize:
                            "calc(0.71rem + var(--large-screen-font-bump))",
                          marginLeft: "13px",
                          marginRight: "5px",
                          color: isNight ? nightTextColor : "inherit",
                          transition: "color 0.35s ease",
                        }}
                      >
                        SEE ALL STEPS
                      </span>
                      <KeyboardDoubleArrowRight
                        sx={{
                          fontSize:
                            "calc(1.76rem + var(--large-screen-font-bump))",
                          width: "16px",
                          height: "1rem",
                          color: isNight ? nightTextColor : dayPrimaryTextColor,
                          verticalAlign: "super",
                          transition: "color 0.35s ease",
                        }}
                      />
                    </div>
                  </Box>
                </Box>

                {subStepsTabs()}

                {/* Step Content */}
                <Box
                  display="flex"
                  justifyContent={"space-between"}
                  height={"85.17px"}
                >
                  {/* Tabs */}
                  <Box>
                    {activeSubStep ? (
                      renderStepContent(activeSubStep)
                    ) : (
                      <Typography>Select a step</Typography>
                    )}
                  </Box>
                  <Box display="flex">
                    <ButtonGroup>
                      <Button
                        disableRipple // prevents ripple covering your transparent background
                        variant="text" // avoids contained background styles
                        sx={{
                          color: canGoBack()
                            ? isNight
                              ? nightTextColor
                              : "#CE1126"
                            : isNight
                              ? nightMutedTextColor
                              : dayMutedTextColor,
                          pointerEvents: canGoBack() ? "auto" : "none",
                          transition: "color 0.35s ease",
                          top: "25px",
                          height: "40px",
                          borderRadius: 0,

                          [theme.breakpoints.up("md")]: {
                            width: "100px",
                          },
                          [theme.breakpoints.up("md_lg")]: {
                            width: "115px",
                          },
                          [theme.breakpoints.up("lg")]: {
                            width: "150px",
                          },
                          [theme.breakpoints.up("xl")]: {
                            width: "180px",
                          },
                          fontSize:
                            "calc(0.71rem + var(--large-screen-font-bump))",
                          border: "0px !important", // removed extra semicolon
                          fontWeight: "bold",

                          // Make background transparent always
                          backgroundColor: "transparent !important",

                          "&:hover": {
                            backgroundColor: "transparent !important",
                          },
                          "&:active": {
                            backgroundColor: "transparent !important",
                            boxShadow: "none !important", // remove elevation shadow on press
                          },
                        }}
                        onClick={() => handleBack()}
                        // onClick={() => {
                        //   if (subSteps.indexOf(activeSubStep) - 1 >= 0)
                        //     setActiveSubStep(
                        //       subSteps[subSteps.indexOf(activeSubStep) - 1]
                        //     );
                        // }}
                      >
                        <Box
                          sx={{
                            marginRight: "20px",
                            height: "10px",
                            width: "10px",
                          }}
                          position={"relative"}
                        >
                          {/* <img
                            style={{
                              position: "absolute",
                              opacity: canGoBack() ? 1 : 0,
                            }}
                            src={"Assets/backwards-arrow-red.svg"}
                            alt="backwards-arrow"
                            height={"10px"}
                          />

                          <img
                            style={{
                              position: "absolute",
                              opacity: canGoBack() ? 0 : 1,
                            }}
                            src={"Assets/backwards-arrow-lightred.svg"}
                            alt="backwards-arrow"
                            height={"10px"}
                          /> */}
                          <ArrowBackIosNewOutlined
                            sx={{
                              color: canGoBack()
                                ? isNight
                                  ? nightTextColor
                                  : "#CE1126"
                                : isNight
                                  ? nightMutedTextColor
                                  : dayMutedTextColor,
                              height: "13px",
                              transition: "color 0.35s ease",
                            }}
                          />
                        </Box>
                        <Typography
                          marginRight={"20px"}
                          fontSize={
                            "calc(0.71rem + var(--large-screen-font-bump))"
                          }
                          fontWeight={"bold"}
                        >
                          BACK
                        </Typography>
                      </Button>

                      <Tooltip
                        enterDelay={100}
                        leaveDelay={100}
                        placement="top"
                        title={
                          steps.indexOf(activeSubStep) !== steps.length - 1 &&
                          !isStepCompleted("subStep", activeSubStep)
                            ? "Compelete the currect step to move to the next"
                            : steps.indexOf(activeSubStep) === 0 &&
                                isStepCompleted("subStep", activeSubStep)
                              ? "You can also press Enter ↵ to proceed"
                              : ""
                        }
                      >
                        <Button
                          elevation={0}
                          variant="contained"
                          onClick={
                            () => pressNextOrReview()
                            // saveUserConfiguration(authUser?.id_token, dataSelection)
                          }
                          sx={(theme) => ({
                            backgroundColor: isStepCompleted(
                              "subStep",
                              activeSubStep,
                            )
                              ? "#CE1126"
                              : isNight
                                ? "#848ba1"
                                : "#e1e1e1",
                            cursor: isStepCompleted("subStep", activeSubStep)
                              ? "pointer"
                              : "default",
                            boxShadow: "none",
                            "&:hover": { boxShadow: "none" },
                            transition: "all 0.5s ease",
                            top: "23px",
                            height: "40px",
                            borderRadius: "0px",
                            color: "white",
                            border: "0px !important",

                            [theme.breakpoints.up("md")]: {
                              width: "100px",
                            },
                            [theme.breakpoints.up("md_lg")]: {
                              width: "115px",
                            },
                            [theme.breakpoints.up("lg")]: {
                              width: "150px",
                            },
                            [theme.breakpoints.up("xl")]: {
                              width: "180px",
                            },
                          })}
                        >
                          <Typography
                            fontSize={
                              "calc(0.71rem + var(--large-screen-font-bump))"
                            }
                            fontWeight={"bold"}
                          >
                            {steps.indexOf(activeStep) === steps.length - 1 &&
                            subSteps.indexOf(activeSubStep) ===
                              subSteps.length - 1
                              ? "REVIEW"
                              : "NEXT"}
                          </Typography>

                          <img
                            style={{ marginLeft: "20px" }}
                            src="Assets/forward-arrow-white.svg"
                            alt="forward-arrow"
                            height={"10px"}
                          />
                        </Button>
                      </Tooltip>
                    </ButtonGroup>
                  </Box>
                </Box>
              </Box>
            </div>
          </MotionBox>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default ConfiguratorTabs;
